import React, { useState, useEffect, useRef, useContext } from "react";
import { FaLinkedin, FaTwitter, FaExclamation, FaBook, FaGraduationCap, FaShieldVirus, FaEnvelope, FaEnvelopeOpen, FaHome, FaHandSparkles, FaMagic, FaChevronDown, FaBars, FaTimes, FaUser, FaSignOutAlt, FaUniversity, FaChartBar, FaPlus, FaImage, FaQuestionCircle, FaClock, FaShieldAlt } from "react-icons/fa";
import phdscanner from "../../assets/phdscanner_logo.webp";
import { useNavigate, useLocation } from "react-router-dom";
import UserProfilePopup from "../UserProfilePopUp/UserProfilePopup";
import UnifiedUserProfile from "../UserProfilePopUp/UnifiedUserProfile";
import { AuthContext } from "../../context/AuthContext";
import useAnalytics from '../../hooks/useAnalytics';
import LanguageSelector from "../LanguageSelector/LanguageSelector";
import { getLoginPath, getDashboardPath, isSaaSSubdomain } from "../../utils/environment";
import { OpportunityService } from "../../services/OpportunityService";
import { UniversityAuthService } from "../../services/UniversityAuthService";

export default function Header() {
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMoreMenuOpen, setMoreMenuOpen] = useState(false);
  const [isUniversityMenuOpen, setUniversityMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [universityUser, setUniversityUser] = useState(null);
  const [universityLogoUrl, setUniversityLogoUrl] = useState(null);
  const [showUniversitySupportModal, setShowUniversitySupportModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const moreMenuButtonRef = useRef(null);
  const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX';
  const { trackEvent } = useAnalytics(GA_MEASUREMENT_ID);
  const [universityName, setUniversityName] = useState("");

  const {
    userDetails: user,
    isUserDetailsLoading: loading,
    handleLogout,
  } = useContext(AuthContext);

  // Check if we're on university routes
  const isUniversityRoute = location.pathname.startsWith('/university/');
  const isUniversityDashboard = location.pathname === '/university/dashboard';
  const isUniversityLoginPage = location.pathname === '/university/login';

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle click outside to close menus
 useEffect(() => {
  const handleClickOutside = (event) => {
    // Check if click is on a submenu item - if so, don't close menu yet
    const isSubmenuItem = event.target.closest('[data-submenu-item]');
    if (isSubmenuItem) {
      // console.log('🎯 Click on submenu item detected, allowing navigation');
      return; // Don't close menu, let the navigation handle it
    }

    if (menuRef.current && !menuRef.current.contains(event.target)) {
      setMoreMenuOpen(false);
      setUniversityMenuOpen(false);
    }

    if (mobileMenuRef.current &&
      !mobileMenuRef.current.contains(event.target) &&
      !event.target.closest('[data-mobile-menu-trigger]')) {
      setMobileMenuOpen(false);
      setMoreMenuOpen(false);
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);

  useEffect(() => {
    async function fetchUniversity() {
      if (isSaaSSubdomain()) {
        try {
          const res = await OpportunityService.getCurrentCustomer();
          if (res && res.university) setUniversityName(res.university);
        } catch {}
      }
    }
    fetchUniversity();
  }, []);

  // Load university user data when on university routes
  useEffect(() => {
    if (isUniversityRoute) {
      // Check if user is actually authenticated (cookie-based)
      const isAuthenticated = UniversityAuthService.isAuthenticated();
      
      if (isAuthenticated) {
        const storedUser = UniversityAuthService.getStoredUser();
        if (storedUser) {
          setUniversityUser(storedUser);
          // Load logo
          try {
            const logoUrl = UniversityAuthService.getLogoUrl(storedUser.university);
            const cacheBustedUrl = `${logoUrl}?t=${Date.now()}`;
            setUniversityLogoUrl(cacheBustedUrl);
          } catch (error) {
            console.error('Failed to load university logo:', error);
          }
        }
      } else {
        // User is not authenticated, clear university user data
        setUniversityUser(null);
        setUniversityLogoUrl(null);
      }
    } else {
      setUniversityUser(null);
      setUniversityLogoUrl(null);
    }
  }, [isUniversityRoute, location.pathname]);

  // Listen for localStorage changes to update university user state
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'university_user' && isUniversityRoute) {
        // University user data changed, reload the data
        const isAuthenticated = UniversityAuthService.isAuthenticated();
        
        if (isAuthenticated) {
          const storedUser = UniversityAuthService.getStoredUser();
          if (storedUser) {
            setUniversityUser(storedUser);
            // Load logo
            try {
              const logoUrl = UniversityAuthService.getLogoUrl(storedUser.university);
              const cacheBustedUrl = `${logoUrl}?t=${Date.now()}`;
              setUniversityLogoUrl(cacheBustedUrl);
            } catch (error) {
              console.error('Failed to load university logo:', error);
            }
          }
        } else {
          setUniversityUser(null);
          setUniversityLogoUrl(null);
        }
      }
    };

    // Listen for storage events (from other tabs/windows)
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events (from same tab)
    window.addEventListener('universityUserUpdated', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('universityUserUpdated', handleStorageChange);
    };
  }, [isUniversityRoute]);

  // University menu with submenu
  const universityMenu = {
    label: "Recruiters",
    id: "university-menu",
    icon: FaUniversity,
    submenu: [
      {
        path: "/university/dashboard",
        label: "Dashboard",
        icon: FaChartBar,
        description: "Manage your opportunities",
        requiresAuth: true
      },
      {
        path: "/university/login",
        label: "Login",
        icon: FaUser,
        description: "Sign in to upload opportunities"
      }
    ]
  };

  const menuItems = isUniversityRoute ? [] : [
    {
      path: "/home",
      label: "Home",
      icon: FaHome,
      description: "Go to homepage"
    },
    {
      path: "/phd-vacancies/standard",
      label: "PhD Openings",
      icon: FaGraduationCap,
      description: "Browse PhD opportunities"
    },
    // University menu (only show on main domain, not SaaS subdomains)
    ...(!isSaaSSubdomain() ? [universityMenu] : []),
    {
      label: "More",
      id: "more-menu",
      icon: FaChevronDown,
      submenu: [
        { path: "/smart-picks", label: "Smart Picks", icon: FaMagic, description: "AI-curated recommendations" },
        { path: "/blogs", label: "PhD Tips", icon: FaBook, description: "Expert advice and tips" },
        { path: "/about", label: "About", icon: FaExclamation, description: "Learn about us" },
        { path: "/contact", label: "Contact", icon: FaEnvelope, description: "Get in touch" },
        ...(user?.role === "admin" ? [{ path: "/admin", label: "Admin", icon: FaShieldVirus, description: "Admin panel" }] : [])
      ]
    }
  ];

  const isActive = (path) => {
    const currentPath = window.location.pathname;
    if (path === '/home') return currentPath === '/' || currentPath === '/home';
    if (path === '/phd-vacancies/standard') return currentPath.startsWith('/phd-vacancies/standard');
    if (path === '/blogs' && currentPath.includes('/blog/')) return true;
    if (path === '/university/login' || path === '/university/signup') return currentPath.startsWith('/university/');
    return currentPath === path;
  };

  const handleNavigation = (path, event, requiresAuth = false, action = null) => {
    // Handle special actions
    if (action === "showUniversitySupport") {
      setShowUniversitySupportModal(true);
      return;
    }

    // Check authentication for dashboard links
    if (requiresAuth && path === "/university/dashboard") {
      const isAuthenticated = UniversityAuthService.isAuthenticated();
      if (!isAuthenticated) {
        // Redirect to login if not authenticated
        path = "/university/login";
      }
    }

    // For SaaS subdomain, rewrite login and dashboard paths
    let finalPath = path;
    if (isSaaSSubdomain()) {
      if (path === "/login") finalPath = getLoginPath();
      if (path === "/dashboard") finalPath = getDashboardPath();
    }
    // console.log('🔄 handleNavigation called:', {
    //   path,
    //   hasEvent: !!event,
    //   currentPath: window.location.pathname,
    //   mobileMenuOpen: isMobileMenuOpen,
    //   moreMenuOpen: isMoreMenuOpen
    // });

    if (event) {
      // console.log('⚡ Preventing default and stopping propagation');
      event.preventDefault();
      event.stopPropagation();
    }

    // Close all menus
    // console.log('🔒 Closing menus...');
    setMobileMenuOpen(false);
    setMoreMenuOpen(false);

    // Track navigation
    // console.log('📊 Tracking navigation event:', path);
    trackEvent({
      category: 'Navigation',
      action: 'Click',
      label: path
    });

    // Force full page reload for PhD Openings to get fresh copy
    if (path === "/phd-vacancies/standard") {
      window.location.href = finalPath;
      return;
    }

    // Navigate with a small delay to ensure state updates are processed first
    // console.log('🚀 Preparing to navigate to:', path);

    // Use setTimeout to ensure the state updates are processed first
    setTimeout(() => {
      try {
        // console.log('🎯 Executing navigation to:', path);
        navigate(finalPath, { replace: false });
        // console.log('✅ Navigation command sent successfully');
      } catch (error) {
        console.error('❌ Navigation failed:', error);
        // Fallback to window.location if navigate fails
        window.location.href = finalPath;
      }
    }, 50);
  };

  // Also add this debugging useEffect to your component:
  useEffect(() => {
    const handleRouteChange = () => {
      // console.log('🗺️ Route actually changed to:', window.location.pathname);
    };

    // Listen for actual route changes
    window.addEventListener('popstate', handleRouteChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  const handleLogoutClick = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setMobileMenuOpen(false);
    setMoreMenuOpen(false);

    trackEvent({ category: 'User', action: 'Logout' });
    handleLogout();
  };

  // Also add logging to the toggleMoreMenu for mobile
  const toggleMoreMenu = (event) => {
    // console.log('📱 Mobile More Menu toggled:', {
    //   currentState: isMoreMenuOpen,
    //   hasEvent: !!event
    // });

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setMoreMenuOpen(!isMoreMenuOpen);
    setUniversityMenuOpen(false); // Close university menu when opening more menu

    // console.log('📱 Mobile More Menu new state:', !isMoreMenuOpen);
  };

  const toggleUniversityMenu = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setUniversityMenuOpen(!isUniversityMenuOpen);
    setMoreMenuOpen(false); // Close more menu when opening university menu
  };

  // Add this useEffect to monitor state changes
  // useEffect(() => {
  //   console.log('📊 State changed:', {
  //     isMobileMenuOpen,
  //     isMoreMenuOpen,
  //     currentPath: window.location.pathname
  //   });
  // }, [isMobileMenuOpen, isMoreMenuOpen]);

  // Add this to monitor navigation changes
  // useEffect(() => {
  //   console.log('🗺️ Location changed to:', window.location.pathname);
  // }, [window.location.pathname]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!isMobileMenuOpen);
    if (!isMobileMenuOpen) {
      setMoreMenuOpen(false);
    }
  };

  // Desktop Menu Item Component
  const DesktopMenuItem = ({ item }) => (
    <button
      onClick={(e) => handleNavigation(item.path, e)}
      className={`group relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-300 hover:scale-105 active:scale-95 ${isActive(item.path)
        ? "text-white bg-gradient-to-r from-[#DB4F40] to-[#C64537] shadow-lg shadow-[#DB4F40]/25"
        : "text-gray-600 hover:text-[#DB4F40] hover:bg-gradient-to-r hover:from-[#FFE5E3]/50 hover:to-[#FFE5E3]/20"
        }`}
      style={{ fontFamily: 'Grocetask, sans-serif' }}
    >
      {item.icon && <item.icon size={16} className="transition-transform group-hover:scale-110" />}
      <span>{item.label}</span>
     
    </button>
  );

  // Mobile Submenu Item Component
  // Replace your MobileSubmenuItem component with this debugging version:

const MobileSubmenuItem = ({ item }) => {
  // console.log('🏗️ MobileSubmenuItem rendered for:', item.path);

  const handleClick = (e) => {
    // console.log('👆 MobileSubmenuItem handleClick CALLED:', {
    //   path: item.path,
    //   label: item.label,
    //   eventType: e.type,
    //   target: e.target.tagName,
    //   currentTarget: e.currentTarget.tagName
    // });

    e.preventDefault();
    e.stopPropagation();
    // console.log('🛑 Event prevented and stopped');

    // Close menus first
    // console.log('🔒 Closing menus from MobileSubmenuItem...');
    setMobileMenuOpen(false);
    setMoreMenuOpen(false);
    setUniversityMenuOpen(false);

    // Track the event
    // console.log('📈 Tracking mobile submenu click');
    trackEvent({
      category: 'Navigation',
      action: 'Click',
      label: `mobile-submenu-${item.path}`
    });

    // Check authentication for dashboard links
    let finalPath = item.path;
    if (item.requiresAuth && item.path === "/university/dashboard") {
      const isAuthenticated = UniversityAuthService.isAuthenticated();
      if (!isAuthenticated) {
        finalPath = "/university/login";
      }
    }

    // Navigate with a small delay
    setTimeout(() => {
      // console.log('🚀 Navigating to:', finalPath);
      try {
        navigate(finalPath);
        // console.log('✅ Navigation successful to:', finalPath);
      } catch (error) {
        // console.error('❌ Navigation failed:', error);
      }
    }, 100);
  };

  // Use mouseUp instead of onClick since onClick is being blocked
  const handleMouseUp = (e) => {
    // console.log('🖱️ MobileSubmenuItem mouseUp - triggering navigation:', item.path);
    
    // Only proceed if this was a left-click (button 0)
    if (e.button !== 0) return;
    
    handleClick(e);
  };

  const handleTouchEnd = (e) => {
    // console.log('📱 MobileSubmenuItem touchEnd - triggering navigation:', item.path);
    handleClick(e);
  };

  return (
    <button
      // Add data attribute for the click-outside handler to recognize
      data-submenu-item="true"
      onMouseUp={handleMouseUp}
      onTouchEnd={handleTouchEnd}
      // Keep onClick as backup
      onClick={handleClick}
      type="button"
      style={{ 
        pointerEvents: 'auto',
        zIndex: 10,
        position: 'relative'
      }}
      className={`group flex items-start gap-3 w-full p-4 text-left rounded-xl transition-all duration-200 ml-4 border-l-2 border-gray-100 ${isActive(item.path)
        ? "text-[#DB4F40] bg-gradient-to-r from-[#FFE5E3]/60 to-[#FFE5E3]/30 border-l-4 border-[#DB4F40]"
        : "text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-50/50 hover:text-[#DB4F40]"
        }`}
    >
      {item.icon && (
        <div className={`flex-shrink-0 p-2 rounded-lg transition-colors ${isActive(item.path)
          ? "bg-[#DB4F40]/20 text-[#DB4F40]"
          : "bg-gray-100 text-gray-500 group-hover:bg-[#DB4F40]/10 group-hover:text-[#DB4F40]"
          }`}>
          <item.icon size={18} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-medium">{item.label}</div>
        {item.description && (
          <div className="text-xs text-gray-500 mt-0.5">{item.description}</div>
        )}
      </div>
    </button>
  );
};

  // Mobile Menu Item Component
  const MobileMenuItem = ({ item }) => (
    <button
      onClick={(e) => handleNavigation(item.path, e)}
      className={`group flex items-start gap-3 w-full p-4 text-left rounded-xl transition-all duration-200 ${isActive(item.path)
        ? "text-[#DB4F40] bg-gradient-to-r from-[#FFE5E3]/60 to-[#FFE5E3]/30 border-l-4 border-[#DB4F40]"
        : "text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-50/50 hover:text-[#DB4F40]"
        }`}
    >
      {item.icon && (
        <div className={`flex-shrink-0 p-2 rounded-lg transition-colors ${isActive(item.path)
          ? "bg-[#DB4F40]/20 text-[#DB4F40]"
          : "bg-gray-100 text-gray-500 group-hover:bg-[#DB4F40]/10 group-hover:text-[#DB4F40]"
          }`}>
          <item.icon size={18} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-medium">{item.label}</div>
        {item.description && (
          <div className="text-xs text-gray-500 mt-0.5">{item.description}</div>
        )}
      </div>
    </button>
  );

  const handleUniversityLogout = async () => {
    try {
      await UniversityAuthService.logout();
      // Clear university user state immediately
      setUniversityUser(null);
      setUniversityLogoUrl(null);
      navigate('/university/login');
    } catch (error) {
      console.error('University logout failed:', error);
      // Even if logout fails, clear the state
      setUniversityUser(null);
      setUniversityLogoUrl(null);
    }
  };

  const renderUserMenu = () => {
    if (loading) {
      return (
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 bg-gray-200 rounded-full animate-pulse"></div>
          <div className="hidden sm:block w-20 h-4 bg-gray-200 rounded animate-pulse"></div>
        </div>
      );
    }

    // Use unified component for both regular and university users
    // Don't show user profile on university login page
    if ((user || universityUser) && !isUniversityLoginPage) {
      return (
        <UnifiedUserProfile 
          user={user} 
          universityUser={universityUser}
          handleLogout={handleLogout}
          handleUniversityLogout={handleUniversityLogout}
          isUniversityRoute={isUniversityRoute}
        />
      );
    }

    // Show sign in button for unauthenticated users
    return (
      <button
        className="group relative overflow-hidden px-6 py-2.5 font-semibold text-white bg-gradient-to-r from-[#DB4F40] to-[#C64537] rounded-xl hover:shadow-lg hover:shadow-[#DB4F40]/25 transition-all duration-300 hover:scale-105 active:scale-95"
        onClick={(e) => {
          trackEvent({ category: 'User', action: isUniversityRoute ? 'University Sign In' : 'Sign In' });
          // Use appropriate login path based on route
          const loginPath = isUniversityRoute ? "/university/login" : (isSaaSSubdomain() ? getLoginPath() : "/login");
          handleNavigation(loginPath, e);
        }}
      >
        <span className="relative z-10">Sign In</span>
        <div className="absolute inset-0 bg-gradient-to-r from-[#C64537] to-[#B73E31] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      </button>
    );
  };

  return (
    <>
      {/* Header */}
      <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${isScrolled
        ? "bg-white/95 backdrop-blur-xl border-b border-gray-200/50 shadow-lg shadow-black/5"
        : "bg-white/90 backdrop-blur-md border-b border-gray-100/50"
        }`}>
        <nav className="px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between mx-auto max-w-7xl">

            {/* Logo */}
            <button
              onClick={(e) => handleNavigation(isUniversityRoute ? "/university/dashboard" : "/home", e)}
              className="group flex items-center gap-3 transition-all duration-300 hover:scale-105 active:scale-95 bg-transparent border-none cursor-pointer"
            >
              <div className="relative">
                {isUniversityRoute && universityLogoUrl ? (
                  <img
                    src={universityLogoUrl}
                    className="h-12 w-12 object-contain rounded-lg border border-gray-200 bg-white p-1 transition-transform group-hover:rotate-0"
                    alt={`${universityUser?.university} Logo`}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : isSaaSSubdomain() && universityName ? (
                  <span className="text-2xl font-extrabold text-[#2C2C2C] notranslate group-hover:text-[#DB4F40] transition-colors" style={{ fontFamily: 'Grocetask, sans-serif' }}>{universityName}</span>
                ) : (
                  <img
                    src={phdscanner}
                    className="h-12 w-auto object-contain transition-transform group-hover:rotate-0"
                    alt="PhDScanner Logo"
                  />
                )}
                <div className="absolute -inset-2 bg-gradient-to-r from-[#DB4F40]/20 to-[#C64537]/20 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl"></div>
              </div>
              {isUniversityRoute ? (
                <div className="flex flex-col">
                  <span className="text-xl font-semibold text-gray-900 group-hover:text-[#DB4F40] transition-colors">
                  {universityUser?.university}
                  </span>
                  <span className="text-sm text-gray-500">
                    PhDScanner Dashboard
                  </span>
                </div>
              ) : !isSaaSSubdomain() && (
                <div className="flex flex-col">
                  <span className="text-2xl font-extrabold text-[#2C2C2C] notranslate group-hover:text-[#DB4F40] transition-colors"
                    style={{ fontFamily: 'Grocetask, sans-serif' }}>
                    PhDScanner
                  </span>
                  <span className="text-xs text-gray-500 font-medium -mt-1 italic">
                    <span className="text-[#DB4F40] font-bold">Find</span> Your Calling
                  </span>
                </div>
              )}
            </button>

            {/* Desktop Navigation */}
            {menuItems.length > 0 && (
              <div className="hidden lg:flex items-center gap-2 flex-1 justify-center mx-8">
                {menuItems.map((item) => (
                  <div key={item.path || item.id} className="relative" ref={item.submenu ? menuRef : null}>
                    {item.submenu ? (
                      <div className="group">
                        <button
                          ref={item.id === "more-menu" ? moreMenuButtonRef : null}
                          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-300 hover:scale-105 active:scale-95 ${(item.id === "more-menu" ? isMoreMenuOpen : isUniversityMenuOpen)
                            ? item.id === "more-menu" 
                              ? "text-[#DB4F40] bg-gradient-to-r from-[#FFE5E3]/50 to-[#FFE5E3]/20"
                              : "text-[#DB4F40]"
                            : item.id === "more-menu"
                              ? "text-gray-600 hover:text-[#DB4F40] hover:bg-gradient-to-r hover:from-[#FFE5E3]/50 hover:to-[#FFE5E3]/20"
                              : "text-gray-600 hover:text-[#DB4F40]"
                            }`}
                          onClick={item.id === "more-menu" ? toggleMoreMenu : toggleUniversityMenu}
                        >
                          {item.icon && <item.icon size={16} className="transition-transform group-hover:scale-110" />}
                          <span>{item.label}</span>
                          <FaChevronDown
                            size={16}
                            className={`transition-all duration-300 ${(item.id === "more-menu" ? isMoreMenuOpen : isUniversityMenuOpen) ? 'rotate-180 text-[#DB4F40]' : 'group-hover:rotate-180'
                              }`}
                          />
                        </button>

                        {/* Desktop Dropdown */}
                        {(item.id === "more-menu" ? isMoreMenuOpen : isUniversityMenuOpen) && (
                          <div className="absolute top-full left-0 mt-2 w-64 bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden animate-in slide-in-from-top-2 duration-200">
                            <div className="p-2">
                              {item.submenu.map((subItem) => (
                                <button
                                  key={subItem.path}
                                  data-submenu-item="true"
                                  onClick={(e) => {
                                    // Close the appropriate menu
                                    if (item.id === "more-menu") {
                                      setMoreMenuOpen(false);
                                    } else {
                                      setUniversityMenuOpen(false);
                                    }
                                    handleNavigation(subItem.path, e, subItem.requiresAuth, subItem.action);
                                  }}
                                  className={`group flex items-start gap-3 w-full p-3 text-left rounded-xl transition-all duration-200 ${isActive(subItem.path)
                                    ? item.id === "more-menu"
                                      ? "text-[#DB4F40] bg-gradient-to-r from-[#FFE5E3]/60 to-[#FFE5E3]/30"
                                      : "text-[#DB4F40]"
                                    : "text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-50/50 hover:text-[#DB4F40]"
                                    }`}
                                >
                                  <div className={`flex-shrink-0 p-2 rounded-lg transition-colors ${isActive(subItem.path)
                                    ? item.id === "more-menu"
                                      ? "bg-[#DB4F40]/20 text-[#DB4F40]"
                                      : "text-[#DB4F40]"
                                    : "bg-gray-100 text-gray-500 group-hover:bg-[#DB4F40]/10 group-hover:text-[#DB4F40]"
                                    }`}>
                                    <subItem.icon size={16} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm">{subItem.label}</div>
                                    <div className="text-xs text-gray-500 mt-0.5">{subItem.description}</div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <DesktopMenuItem item={item} />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Right Section */}
            <div className="flex items-center gap-4">
              {/* Back to PhDScanner - Only show on university routes */}
              {isUniversityRoute && (
                <div className="hidden lg:block">
                  <button
                    onClick={(e) => handleNavigation("/home", e)}
                    className="group flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-[#DB4F40] hover:bg-gradient-to-r hover:from-[#FFE5E3]/50 hover:to-[#FFE5E3]/20 rounded-xl transition-all duration-300 hover:scale-105 active:scale-95"
                    style={{ fontFamily: 'Grocetask, sans-serif' }}
                  >
                    <FaHome size={16} className="transition-transform group-hover:scale-110" />
                    <span>Back to PhDScanner</span>
                  </button>
                </div>
              )}

              {/* Desktop Language Selector */}
              <div className="hidden lg:block">
                <LanguageSelector />
              </div>

              {/* Desktop User Menu */}
              <div className="hidden lg:block">
                {renderUserMenu()}
              </div>

              {/* Mobile Menu Button */}
              <button
                data-mobile-menu-trigger
                onClick={toggleMobileMenu}
                className={`lg:hidden relative p-3 rounded-xl transition-all duration-300 hover:scale-105 active:scale-95 ${isMobileMenuOpen
                  ? "text-[#DB4F40] bg-gradient-to-r from-[#FFE5E3]/50 to-[#FFE5E3]/20"
                  : "text-gray-600 hover:text-[#DB4F40] hover:bg-gray-50"
                  }`}
                aria-label="Toggle mobile menu"
              >
                <div className="relative">
                  {isMobileMenuOpen ? (
                    <FaTimes className="w-6 h-6 transition-transform duration-300 rotate-90" />
                  ) : (
                    <FaBars className="w-6 h-6 transition-transform duration-300" />
                  )}
                </div>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div
              className="lg:hidden mt-6 animate-in slide-in-from-top-4 duration-300"
              ref={mobileMenuRef}
            >
              <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden">

                {/* Back to PhDScanner - Mobile */}
                {isUniversityRoute && (
                  <div className="p-4 border-b border-gray-100/50">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-1 h-6 bg-gradient-to-b from-[#DB4F40] to-[#C64537] rounded-full"></div>
                      <span className="font-medium text-gray-700">Navigation</span>
                    </div>
                    <button
                      onClick={(e) => handleNavigation("/home", e)}
                      className="group flex items-center gap-3 w-full p-4 text-left rounded-xl transition-all duration-200 text-gray-700 hover:bg-gradient-to-r hover:from-[#FFE5E3]/20 hover:to-[#FFE5E3]/10 hover:text-[#DB4F40]"
                    >
                      <div className="p-2 rounded-lg bg-gray-100 text-gray-500 group-hover:bg-[#DB4F40]/10 group-hover:text-[#DB4F40] transition-colors">
                        <FaHome size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">Back to PhDScanner</div>
                        <div className="text-xs text-gray-500 mt-0.5">Return to main site</div>
                      </div>
                    </button>
                  </div>
                )}

                {/* Mobile Language Selector */}
                <div className="p-4 border-b border-gray-100/50">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-1 h-6 bg-gradient-to-b from-[#DB4F40] to-[#C64537] rounded-full"></div>
                    <span className="font-medium text-gray-700">Language</span>
                  </div>
                  <LanguageSelector isMobile />
                </div>

                {/* Mobile Menu Items */}
                {menuItems.length > 0 && (
                  <div className="p-4 space-y-2">
                    {menuItems.map((item) => (
                      <div key={item.path || item.id}>
                        {item.submenu ? (
                          <div className="space-y-2">
                            {/* More Menu Toggle */}
                            <button
                              className={`flex items-center justify-between w-full p-4 text-left rounded-xl transition-all duration-200 ${(item.id === "more-menu" ? isMoreMenuOpen : isUniversityMenuOpen)
                                ? item.id === "more-menu"
                                  ? "text-[#DB4F40] bg-gradient-to-r from-[#FFE5E3]/50 to-[#FFE5E3]/20"
                                  : "text-[#DB4F40]"
                                : item.id === "more-menu"
                                  ? "text-gray-700 hover:bg-gray-50"
                                  : "text-gray-700 hover:text-[#DB4F40]"
                                }`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (item.id === "more-menu") {
                                  setMoreMenuOpen(!isMoreMenuOpen);
                                  setUniversityMenuOpen(false);
                                } else {
                                  setUniversityMenuOpen(!isUniversityMenuOpen);
                                  setMoreMenuOpen(false);
                                }
                              }}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg transition-colors ${(item.id === "more-menu" ? isMoreMenuOpen : isUniversityMenuOpen)
                                  ? item.id === "more-menu"
                                    ? "bg-[#DB4F40]/20 text-[#DB4F40]"
                                    : "text-[#DB4F40]"
                                  : "bg-gray-100 text-gray-500"
                                  }`}>
                                  {item.icon && <item.icon size={18} />}
                                </div>
                                <span className="font-medium">{item.label}</span>
                              </div>
                              <FaChevronDown
                                size={20}
                                className={`transition-transform duration-300 ${(item.id === "more-menu" ? isMoreMenuOpen : isUniversityMenuOpen) ? 'rotate-180 text-[#DB4F40]' : 'text-gray-400'
                                  }`}
                              />
                            </button>

                            {/* Submenu Items */}
                            {(item.id === "more-menu" ? isMoreMenuOpen : isUniversityMenuOpen) && (
                              <div className="space-y-1 animate-in slide-in-from-top-2 duration-200">
                                {/* {console.log('🔄 Rendering mobile submenu items:', item.submenu.length)} */}
                                {item.submenu.map((subItem) => {
                                  // console.log('🏗️ Creating MobileSubmenuItem for:', subItem.path);
                                  return (
                                    <MobileSubmenuItem key={subItem.path} item={subItem} />
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ) : (
                          <MobileMenuItem item={item} />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Mobile User Section */}
                <div className="border-t border-gray-100/50 p-4">
                  {loading ? (
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gray-200 rounded-xl animate-pulse"></div>
                      <div className="flex-1 space-y-2">
                        <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
                        <div className="w-32 h-3 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                    </div>
                  ) : (user || universityUser) && !isUniversityLoginPage ? (
                    <div className="space-y-3">
                      {/* User Info */}
                      <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-gray-50 to-gray-50/50 rounded-xl">
                        {isUniversityRoute && universityUser ? (
                          <div className="w-12 h-12 bg-gradient-to-r from-[#DB4F40] to-[#C64537] rounded-xl flex items-center justify-center text-white shadow-lg shadow-[#DB4F40]/25">
                            <FaUniversity className="w-6 h-6" />
                          </div>
                        ) : (
                          <div className="w-12 h-12 bg-gradient-to-r from-[#DB4F40] to-[#C64537] rounded-xl flex items-center justify-center text-white shadow-lg shadow-[#DB4F40]/25">
                            <FaUser className="w-6 h-6" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-900 truncate">
                            {isUniversityRoute && universityUser 
                              ? `${universityUser.firstName} ${universityUser.lastName}`
                              : (user?.name || user?.email)
                            }
                          </div>
                          <div className="text-sm text-gray-500 truncate">
                            {isUniversityRoute && universityUser ? universityUser.email : user?.email}
                          </div>
                          {isUniversityRoute && universityUser && (
                            <div className="text-xs text-gray-400 truncate">
                              {universityUser.position} - {universityUser.department}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Logout Button */}
                      <button
                        onClick={isUniversityRoute ? handleUniversityLogout : handleLogoutClick}
                        className="group flex items-center gap-3 w-full p-4 text-left text-gray-700 rounded-xl hover:bg-red-50 hover:text-red-600 transition-all duration-200"
                      >
                        <div className="p-2 rounded-lg bg-gray-100 text-gray-500 group-hover:bg-red-100 group-hover:text-red-500 transition-colors">
                          <FaSignOutAlt className="w-5 h-5" />
                        </div>
                        <span className="font-medium">Sign out</span>
                      </button>
                    </div>
                  ) : (
                    <button
                      className="group relative overflow-hidden w-full py-4 font-semibold text-white bg-gradient-to-r from-[#DB4F40] to-[#C64537] rounded-xl hover:shadow-lg hover:shadow-[#DB4F40]/25 transition-all duration-300 active:scale-95"
                      onClick={(e) => {
                        trackEvent({ category: 'User', action: isUniversityRoute ? 'University Sign In (Mobile)' : 'Sign In (Mobile)' });
                        // Use appropriate login path based on route
                        const loginPath = isUniversityRoute ? "/university/login" : (isSaaSSubdomain() ? getLoginPath() : "/login");
                        handleNavigation(loginPath, e);
                      }}
                    >
                      <span className="relative z-10">Sign In</span>
                      <div className="absolute inset-0 bg-gradient-to-r from-[#C64537] to-[#B73E31] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-20 lg:h-24"></div>

      {/* University Support Modal */}
      {showUniversitySupportModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#FFD6D4] to-[#FFEEED] px-6 py-4 border-b border-[#FFD6D4] rounded-t-3xl">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-[#DB4F40] flex items-center">
                  <FaUniversity className="mr-3" />
                  University Support
                </h2>
                <button
                  onClick={() => setShowUniversitySupportModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FaTimes size={20} />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-8">
              <div className="space-y-6">
                {/* Main Contact Message */}
                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
                  <div className="flex items-start">
                    <FaQuestionCircle className="text-blue-600 text-2xl mr-4 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-semibold text-blue-900 mb-3">Need Help with University Access?</h3>
                      <p className="text-blue-800 leading-relaxed mb-4">
                        If you're unable to access or login using your university email, please contact our support team for assistance.
                      </p>
                      <div className="bg-white rounded-xl p-4 border border-blue-200">
                        <p className="text-sm font-medium text-blue-900 mb-2">Contact our support team:</p>
                        <a 
                          href="mailto:admin@phdscanner.com" 
                          className="text-lg font-bold text-[#DB4F40] hover:text-[#C64537] transition-colors duration-200 flex items-center"
                        >
                          <FaEnvelope className="mr-2" />
                          admin@phdscanner.com
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Support Options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                    <h4 className="text-lg font-semibold text-[#1B222A] mb-3 flex items-center">
                      <FaEnvelope className="text-[#DB4F40] mr-2" />
                      Email Support
                    </h4>
                    <p className="text-gray-600 mb-4">
                      Send us an email with your university details and we'll help you get access.
                    </p>
                    <a 
                      href="mailto:admin@phdscanner.com?subject=University Access Support&body=Hello, I need help accessing my university account. Please provide your university name and email address."
                      className="inline-flex items-center px-4 py-2 bg-[#DB4F40] text-white rounded-lg hover:bg-[#C64537] transition-colors duration-200 text-sm font-medium"
                    >
                      Send Email
                    </a>
                  </div>

                  <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                    <h4 className="text-lg font-semibold text-[#1B222A] mb-3 flex items-center">
                      <FaQuestionCircle className="text-[#DB4F40] mr-2" />
                      Common Issues
                    </h4>
                    <ul className="text-gray-600 space-y-2 text-sm">
                      <li>• University email not recognized</li>
                      <li>• Account verification problems</li>
                      <li>• Login access issues</li>
                      <li>• Password reset assistance</li>
                    </ul>
                  </div>
                </div>

                {/* Response Time Information */}
                <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                    <div>
                      <h4 className="text-lg font-semibold text-green-900 mb-1">Response Time</h4>
                      <p className="text-green-800">
                        We typically respond to support requests within 24 hours during business days.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
